import { useId, useMemo, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import AppButton from "../../../ui/buttons/AppButton";
import { formatTimeAgo } from "../../../shared/formatting/relativeTimes";
import EmptyState from "../../../ui/feedback/EmptyState";
import SuggestionList from "../../../ui/forms/SuggestionList";
import { nextSuggestionIndex, suggestionOptionId } from "../../../ui/forms/suggestionNavigation";
import { FORM_CONTROL_CLASS, FORM_HELP_TEXT_CLASS } from "../../../ui/styles/formStyles";
import { changePreferences, useUserPreferences } from "../../profile/preferences/userPreferences";
import RefreshFactsButton from "../hosting/RefreshFactsButton";
import type { TurnRoute } from "../hosting/routeChoices";
import { gapsText } from "../hosting/routeFitText";
import { choosesRoute, isStarred, llmOptionsOf, matchesSearch, withEffort, type ConnectorOptions, type LlmOption } from "./llmOptions";

export interface LlmChoice {
  readonly connectorId: string;
  readonly route: TurnRoute;
}

interface LlmPickerProps {
  /** The PCs whose LLMs it lists, with the server's route options: its owner's (`useLlmChoices`). */
  choices: readonly ConnectorOptions[];
  /** Reads the PCs' reports and the route options again, keeping these on screen. */
  onRefresh: () => void;
  value: LlmChoice | null;
  onChoose: (choice: LlmChoice) => void;
  /** Below the list, such as a step's way back to the Thread's default. */
  footer?: ReactNode;
}

type Tab = "starred" | "all";

/**
 * The one LLM picker (F106, Design C), for an agent step and for the Thread's default, built
 * like T3 Code's: search, a Starred tab and All, grouped by PC and agent or API key. LLMs the
 * server finds short of the requirements are greyed out with the reason; an agent's reasoning
 * efforts show when it offers them. Lists come from each PC's latest report: "seen" and Refresh
 * read them again. Never a list in code.
 */
export default function LlmPicker({ choices, onRefresh, value, onChoose, footer }: LlmPickerProps) {
  const { t, i18n } = useTranslation();
  const { preferences } = useUserPreferences();
  const stars = preferences.starredLlms;
  // Until the person picks a tab: Starred once their stars are read, if they have any.
  const [pickedTab, setTab] = useState<Tab | null>(null);
  const tab = pickedTab ?? (stars.length > 0 ? "starred" : "all");
  const [search, setSearch] = useState("");
  const [active, setActive] = useState(0);
  const listId = useId();

  const options = useMemo(() => llmOptionsOf(choices), [choices]);

  const shown = options.filter((option) => (tab === "all" || isStarred(stars, option.star)) && matchesSearch(option, search));
  const chosen = options.find((option) => choosesRoute(option, value?.route ?? null, value?.connectorId ?? null));
  const effort = value?.route.kind === "agent" ? (value.route.reasoning_effort ?? null) : null;
  const choose = (option: LlmOption) => {
    if (blockedReason(option) !== null) return;
    // An effort carries over to an LLM of the same agent, which offers the same ones.
    onChoose({ connectorId: option.connectorId, route: withEffort(option.route, option.efforts.some((offered) => offered.effortId === effort) ? effort : null) });
  };
  const blockedReason = (option: LlmOption): string | null =>
    option.unavailableBecause !== null ? t(`threads.hosting.unavailable.${option.unavailableBecause}`) : option.fit.kind === "lacks" ? gapsText(t, option.fit.gaps) : null;
  const shownPc = choices.find((choice) => choice.connector.connectorId === (value?.connectorId ?? choices[0]?.connector.connectorId));

  return (
    <div className="space-y-2">
      <input
        type="search"
        role="combobox"
        aria-expanded={true}
        aria-controls={listId}
        aria-activedescendant={shown.length > 0 ? suggestionOptionId(listId, Math.min(active, shown.length - 1)) : undefined}
        aria-label={t("threads.llm.search")}
        placeholder={t("threads.llm.search")}
        className={FORM_CONTROL_CLASS}
        value={search}
        onChange={(event) => { setSearch(event.target.value); setActive(0); }}
        onKeyDown={(event) => {
          const next = nextSuggestionIndex(event.key, active, shown.length);
          if (next !== null) {
            event.preventDefault();
            setActive(next);
          } else if (event.key === "Enter") {
            const option = shown[active];
            if (option !== undefined) {
              event.preventDefault();
              choose(option);
            }
          }
        }}
      />
      <div className="flex gap-1" role="group" aria-label={t("threads.llm.show")}>
        {(["starred", "all"] as const).map((name) => (
          <AppButton key={name} variant={tab === name ? "primary" : "secondary"} size="xs" aria-pressed={tab === name} onClick={() => { setTab(name); setActive(0); }}>
            {t(`threads.llm.tabs.${name}`)}
          </AppButton>
        ))}
      </div>
      {shown.length === 0 ? (
        <EmptyState size="small">{t(tab === "starred" && search === "" ? "threads.llm.noStars" : "threads.llm.noMatch")}</EmptyState>
      ) : (
        <SuggestionList
          id={listId}
          label={t("threads.llm.list")}
          items={shown}
          activeIndex={Math.min(active, shown.length - 1)}
          itemKey={(option) => option.key}
          groupOf={(option) => option.group}
          isDisabled={(option) => blockedReason(option) !== null}
          onChoose={choose}
          placement="inPanel"
          renderItem={(option) => (
            <LlmRow
              option={option}
              chosen={option === chosen}
              starred={isStarred(stars, option.star)}
              note={blockedReason(option) ?? (option.fit.kind === "uncertain" ? gapsText(t, option.fit.gaps) : null)}
              onStar={() => {
                // One star action, never the whole list: another device's stars stay (BA16-04).
                if (option.star !== null) void changePreferences({ starredLlm: { llm: option.star, starred: !isStarred(stars, option.star) } });
              }}
            />
          )}
        />
      )}
      {chosen !== undefined && chosen.efforts.length > 0 && value !== null ? (
        <div className="flex flex-wrap items-center gap-1" role="group" aria-label={t("threads.llm.effort")}>
          <span className={FORM_HELP_TEXT_CLASS}>{t("threads.llm.effort")}</span>
          {[null, ...chosen.efforts].map((offered) => {
            const id = offered?.effortId ?? null;
            return (
              <AppButton key={id ?? ""} variant={effort === id ? "primary" : "secondary"} size="xs" aria-pressed={effort === id} onClick={() => { onChoose({ ...value, route: withEffort(value.route, id) }); }}>
                {offered?.displayName ?? t("threads.llm.default")}
              </AppButton>
            );
          })}
        </div>
      ) : null}
      {shownPc === undefined ? null : (
        <div className="flex flex-wrap items-center gap-2">
          {shownPc.listing.reportReceivedAt === null ? null : (
            <span className={FORM_HELP_TEXT_CLASS}>{t("threads.llm.seen", { time: formatTimeAgo(shownPc.listing.reportReceivedAt, i18n.language) })}</span>
          )}
          <RefreshFactsButton connectorId={shownPc.connector.connectorId} route={value?.route ?? null} reportReceivedAt={shownPc.listing.reportReceivedAt} onReload={onRefresh} />
        </div>
      )}
      {footer}
    </div>
  );
}

function LlmRow({ option, chosen, starred, note, onStar }: { option: LlmOption; chosen: boolean; starred: boolean; note: string | null; onStar: () => void }) {
  const { t } = useTranslation();
  const name = option.modelName ?? (option.defaultModelName === null ? t("threads.llm.default") : t("threads.llm.defaultNamed", { model: option.defaultModelName }));
  return (
    <span className="flex items-center gap-2">
      {option.star === null ? (
        <span className="w-4" aria-hidden="true" />
      ) : (
        <button
          type="button"
          tabIndex={-1}
          className="w-4 text-center"
          aria-label={t(starred ? "threads.llm.unstar" : "threads.llm.star", { model: name })}
          aria-pressed={starred}
          onMouseDown={(event) => { event.preventDefault(); event.stopPropagation(); }}
          onClick={(event) => { event.stopPropagation(); onStar(); }}
        >
          {starred ? "★" : "☆"}
        </button>
      )}
      <span className="min-w-0 flex-1 truncate">{name}</span>
      {note === null ? null : <span className="app-help-text shrink-0 text-xs">{note}</span>}
      {chosen ? <span aria-label={t("threads.llm.chosen")}>✓</span> : null}
    </span>
  );
}
